import { Target, Users, Award, TrendingUp, BarChart3 } from "lucide-react";

export const getAnalysisQuestions = (
  entity1Type,
  entity2Type,
  activeComparisonTab,
  entities
) => {
  // Don't show analysis questions for people comparison tab
  if (activeComparisonTab === "people") {
    return [];
  }

  if (activeComparisonTab === "entities") {
    const entity1Name = entities[0]?.name;
    const entity2Name = entities[1]?.name;

    // Teams vs Teams Compare
    if (entity1Type === "team" && entity2Type === "team") {
      return [
        {
          id: "Soft Skills Gaps",
          title: "Soft Skills Gaps",
          description:
            "Identify which team lacks which skills and how this affects collaboration or delivery.",
          icon: Target,
        },
        {
          id: "Personality Distribution",
          title: "Personality Distribution",
          description:
            "Analyze team compositions based on spirit, profession, rewards, and passion to infer performance traits or cultural dynamics.",
          icon: Users,
        },
        {
          id: "Leadership Metrics",
          title: "Leadership Metrics",
          description:
            "Compare how many members are ready or eligible for leadership roles; this highlights maturity and growth potential.",
          icon: Award,
        },
      ];
    }

    // Departments vs Departments Compare
    else if (entity1Type === "department" && entity2Type === "department") {
      return [
        {
          id: "internal-mobility-metrics",
          title: "Internal Mobility Metrics",
          description:
            "Determine how effective departments are in enabling cross-role or cross-level movements.",
          icon: TrendingUp,
        },
        {
          id: "aggregate-skill-gaps",
          title: "Aggregate Skill Gaps",
          description:
            "Understand skill deficiencies at a departmental level, useful for L&D planning.",
          icon: Target,
        },
        {
          id: "upskilling-readiness",
          title: "Upskilling Readiness",
          description:
            "Assess average upskilling scores to see which departments are better prepared or more invested in talent growth.",
          icon: BarChart3,
        },
      ];
    }

    // Roles vs Roles Compare
    else if (entity1Type === "role" && entity2Type === "role") {
      return [
        {
          id: "required-vs-available-skills",
          title: "Required vs Available Skills",
          description:
            "Identify which role has a greater skill gap across the organization.",
          icon: Target,
        },
        {
          id: "soft-skills-benchmarking",
          title: "Soft Skills Benchmarking",
          description:
            "Understand behavioral or interpersonal requirements of roles and how talent stacks up.",
          icon: Users,
        },
        {
          id: "personality-fit-profiles",
          title: "Personality Fit Profiles",
          description:
            "Suggest ideal personality vectors for each role and compare how different roles align or diverge.",
          icon: Award,
        },
      ];
    }

    // Users vs Users Compare
    else if (entity1Type === "people" && entity2Type === "people") {
      return [
        {
          id: "leadership-potential",
          title: "Leadership Potential",
          description:
            "Use leadership scores to identify who is more suited for management or mentorship roles.",
          icon: Award,
        },
        {
          id: "skill-gap-delta",
          title: "Skill Gap Delta",
          description:
            "Contrast the gap between current and expected skills for similar or adjacent roles.",
          icon: Target,
        },
        {
          id: "mobility-readiness",
          title: "Mobility Readiness",
          description:
            "Evaluate internal movement potential—who could move to another role with minimal training.",
          icon: TrendingUp,
        },
      ];
    }

    // Team vs Department Compare
    else if (
      (entity1Type === "team" && entity2Type === "department") ||
      (entity1Type === "department" && entity2Type === "team")
    ) {
      return [
        {
          id: "talent-pipeline",
          title: "Talent Pipeline",
          description:
            "Assess how a team feeds into departmental growth or lacks alignment with departmental skill strategies.",
          icon: TrendingUp,
        },
        {
          id: "personality-alignment",
          title: "Personality Alignment",
          description:
            "Detect mismatches between team traits and departmental leadership or culture.",
          icon: Users,
        },
        {
          id: "cross-skill-influence",
          title: "Cross-Skill Influence",
          description:
            "Gauge if the team strengthens or weakens department-wide soft or job skills.",
          icon: Target,
        },
      ];
    }

    // Role vs Team Compare
    else if (
      (entity1Type === "role" && entity2Type === "team") ||
      (entity1Type === "team" && entity2Type === "role")
    ) {
      return [
        {
          id: "role-coverage-in-team",
          title: "Role Coverage in Team",
          description:
            "Evaluate if the role is fully represented and whether those in that role meet expectations.",
          icon: Target,
        },
        {
          id: "skill-gap-concentration",
          title: "Skill Gap Concentration",
          description:
            "Identify whether a specific team holding a role shows critical deficiencies.",
          icon: BarChart3,
        },
        {
          id: "personality-to-role-fit",
          title: "Personality-to-Role Fit",
          description:
            "Map whether team members in a role align with the personality profile associated with high performers.",
          icon: Users,
        },
      ];
    }

    // User vs Role Compare
    else if (
      (entity1Type === "people" && entity2Type === "role") ||
      (entity1Type === "role" && entity2Type === "people")
    ) {
      return [
        {
          id: "skill-match-index",
          title: "Skill Match Index",
          description:
            "Quantify how well a user fits the skill requirements of a role.",
          icon: Target,
        },
        {
          id: "personality-fit-for-role",
          title: "Personality Fit for Role",
          description:
            "Predict role success based on how closely a user's personality vector matches the role's success profile.",
          icon: Award,
        },
        {
          id: "mobility-feasibility",
          title: "Mobility Feasibility",
          description:
            "Estimate training effort or time required for the user to transition into the target role.",
          icon: TrendingUp,
        },
      ];
    }

    // User vs Team Compare
    else if (
      (entity1Type === "people" && entity2Type === "team") ||
      (entity1Type === "team" && entity2Type === "people")
    ) {
      return [
        {
          id: "soft-skills-complementarity",
          title: "Soft Skills Complementarity",
          description:
            "Determine if the user's strengths fill the team's weaknesses.",
          icon: Users,
        },
        {
          id: "leadership-contrast",
          title: "Leadership Contrast",
          description:
            "Compare leadership indicators to identify mentoring or promotion opportunities.",
          icon: Award,
        },
        {
          id: "team-cultural-fit",
          title: "Team Cultural Fit",
          description:
            "Use personality vectors to evaluate potential friction or harmony with team dynamics.",
          icon: Target,
        },
      ];
    }

    // Default fallback for other combinations
    else {
      return [
        {
          id: "comprehensive-analysis",
          title: "Comprehensive Analysis",
          description:
            "Detailed comparison across all key metrics and dimensions",
          icon: BarChart3,
        },
        {
          id: "strengths-weaknesses",
          title: "Strengths & Weaknesses",
          description: "Identify relative strengths and areas for improvement",
          icon: Award,
        },
        {
          id: "strategic-insights",
          title: "Strategic Insights",
          description:
            "Generate actionable insights for strategic decision making",
          icon: Target,
        },
      ];
    }
  }

  return [];
};
